import {
  MOVEMENT_LABELS,
  csvCell,
  type InventoryKind,
  type MovementKind,
  type StockTracking,
} from "./stock.js";
import { CONDITION_LABELS, STATUS_LABELS, type ItemCondition, type ItemStatus } from "./types.js";
import { priceFromCents } from "./validate.js";

/**
 * Reports you build (Stage 6): pick what to look at, how to group it, what to
 * total and what to leave out, and get a table, a CSV, and a name to come back
 * to it by.
 *
 * The server sends **facts** -- one flat row per place a thing's units are,
 * and one per movement in a date range -- and everything else happens here,
 * in pure functions: grouping, filtering, totals, the insights, the CSV. So a
 * chip change regroups on the spot without a round trip, the node suite tests
 * the arithmetic, and the Durable Object does nothing cleverer than a join.
 *
 * Two sources:
 *
 * - **holdings**, what you hold now. A thing's units are split by where they
 *   are: an ordinary item is one row; a stockroom item is a row for the shelf
 *   and one per client it is out at, so "by client" and "on the shelf versus
 *   deployed" fall out of the same rows. Sold, discarded and retired things
 *   are in the facts but left out unless asked for.
 * - **activity**, what happened in a date range: the stockroom's movement
 *   history (received, deployed, returned, retired, held, released). A
 *   personal inventory has no movements, so it has only holdings.
 */

/* ---------------------------------------------------------------- facts -- */

/** Where a holding's units are. `item` is anything that is not stock. */
export type HoldingPlace = "item" | "shelf" | "client" | "retired";

export const PLACE_LABELS: Record<HoldingPlace, string> = {
  item: "Not stock",
  shelf: "On the shelf",
  client: "Out at a client",
  retired: "Retired",
};

export interface HoldingFact {
  itemId: string;
  title: string;
  categoryId: string | null;
  locationId: string | null;
  tagIds: string[];
  status: ItemStatus;
  condition: ItemCondition;
  place: HoldingPlace;
  /** The client the units are out at; null anywhere else. */
  clientId: string | null;
  /** The vendor on the receipt it came in on; "" when there is none. */
  vendor: string;
  tracking: StockTracking | null;
  units: number;
  /**
   * The item's own price. Each for stock; for an ordinary item (`place` is
   * `item`) the price of the whole item, whatever its quantity. Null when
   * nobody priced it.
   */
  priceCents: number | null;
  /** Each. Private: the receipt's cost, or what a deployment froze. */
  costCents: number | null;
  /** The day these units got where they are: received, deployed, retired, or the item made. */
  since: string;
  /** Sold, discarded or retired: out of "what you hold" unless asked for. */
  gone: boolean;
}

export interface ActivityFact {
  itemId: string;
  title: string;
  categoryId: string | null;
  kind: MovementKind;
  /** The day it happened, as the person gave it. */
  on: string;
  units: number;
  clientId: string | null;
  vendor: string;
  /** Each: what it was billed at, on a deployment. */
  priceCents: number | null;
  /** Each. Private. */
  costCents: number | null;
}

/** `GET /api/reports/facts?from=&to=`. Activity covers `from` to `to`, both days included. */
export interface ReportFacts {
  holdings: HoldingFact[];
  activity: ActivityFact[];
  from: string;
  to: string;
  clients: { id: string; name: string }[];
}

/* ----------------------------------------------------------------- spec -- */

export type ReportSource = "holdings" | "activity";

export type ReportDimension =
  | "category"
  | "location"
  | "tag"
  | "status"
  | "condition"
  | "place"
  | "client"
  | "vendor"
  | "tracking"
  | "item"
  | "month"
  | "kind";

export const DIMENSION_LABELS: Record<ReportDimension, string> = {
  category: "Category",
  location: "Location",
  tag: "Tag",
  status: "Status",
  condition: "Condition",
  place: "Where",
  client: "Client",
  vendor: "Vendor",
  tracking: "Kind",
  item: "Item",
  month: "Month",
  kind: "What happened",
};

/** What each source can be grouped and filtered by, in each kind of inventory, in menu order. */
export function reportDimensions(source: ReportSource, kind: InventoryKind): ReportDimension[] {
  if (source === "activity") return ["month", "kind", "client", "vendor", "category", "item"];
  return kind === "stock"
    ? ["category", "place", "client", "location", "tag", "vendor", "tracking", "condition", "item"]
    : ["category", "location", "tag", "status", "condition", "item"];
}

export type ReportMeasure = "rows" | "units" | "value" | "cost";

export const REPORT_MEASURES: readonly ReportMeasure[] = ["rows", "units", "value", "cost"];

export function measureLabel(measure: ReportMeasure, source: ReportSource): string {
  switch (measure) {
    case "rows":
      return source === "activity" ? "Lines" : "Items";
    case "units":
      return "Units";
    case "value":
      return source === "activity" ? "Billed" : "Value";
    case "cost":
      return "Cost";
  }
}

export type ReportRangePreset = "this_month" | "last_month" | "30d" | "90d" | "ytd" | "12m" | "custom";

export const RANGE_LABELS: Record<ReportRangePreset, string> = {
  this_month: "This month",
  last_month: "Last month",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  ytd: "This year",
  "12m": "Last 12 months",
  custom: "Between dates",
};

export interface ReportRange {
  preset: ReportRangePreset;
  /** Only for `custom`. YYYY-MM-DD. */
  from?: string;
  to?: string;
}

export interface ReportSort {
  /** "label" sorts by the first column; a measure sorts by that total. */
  by: "label" | ReportMeasure;
  dir: "asc" | "desc";
}

export interface ReportSpec {
  source: ReportSource;
  groupBy: ReportDimension;
  /** A second level: client, then month. */
  thenBy: ReportDimension | null;
  measures: ReportMeasure[];
  /** One value per dimension; "" means "has none" (no category, not filed, no client). */
  filters: Partial<Record<ReportDimension, string>>;
  /** Holdings only: count what was sold, discarded or retired too. */
  includeGone: boolean;
  /** Activity only. */
  range: ReportRange;
  sort: ReportSort;
}

/** The totals a kind of inventory has: a personal one keeps no costs. */
export const reportMeasures = (kind: InventoryKind): ReportMeasure[] =>
  kind === "stock" ? [...REPORT_MEASURES] : REPORT_MEASURES.filter((measure) => measure !== "cost");

/** The report the builder opens on. */
export function defaultReportSpec(kind: InventoryKind): ReportSpec {
  return {
    source: "holdings",
    groupBy: "category",
    thenBy: null,
    measures: kind === "stock" ? ["rows", "units", "cost"] : ["rows", "units", "value"],
    filters: {},
    includeGone: false,
    range: { preset: "12m" },
    sort: { by: "label", dir: "asc" },
  };
}

/** A saved report: a name and a spec, kept in the inventory for every member. */
export interface SavedReport {
  id: string;
  name: string;
  spec: ReportSpec;
  createdAt: number;
  /** The email of whoever saved it. */
  createdBy: string;
}

export const MAX_SAVED_REPORTS = 50;

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Anything (a saved row, a request body) as a spec that will run: every
 * unknown or unavailable choice falls back to the default's, so an old saved
 * report keeps opening after the builder changes.
 */
export function normaliseReportSpec(value: unknown, kind: InventoryKind): ReportSpec {
  const base = defaultReportSpec(kind);
  if (!value || typeof value !== "object") return base;
  const raw = value as Record<string, unknown>;

  const source: ReportSource = raw["source"] === "activity" && kind === "stock" ? "activity" : "holdings";
  const allowed = reportDimensions(source, kind);
  const dimension = (candidate: unknown): ReportDimension | null =>
    typeof candidate === "string" && (allowed as string[]).includes(candidate)
      ? (candidate as ReportDimension)
      : null;

  const groupBy = dimension(raw["groupBy"]) ?? allowed[0]!;
  const thenBy = dimension(raw["thenBy"]);
  const measures = Array.isArray(raw["measures"])
    ? reportMeasures(kind).filter((measure) => (raw["measures"] as unknown[]).includes(measure))
    : base.measures;

  const filters: Partial<Record<ReportDimension, string>> = {};
  if (raw["filters"] && typeof raw["filters"] === "object") {
    for (const [key, filter] of Object.entries(raw["filters"] as Record<string, unknown>)) {
      const dim = dimension(key);
      if (dim && dim !== "item" && typeof filter === "string") filters[dim] = filter.slice(0, 200);
    }
  }

  const rawRange = (raw["range"] ?? {}) as Record<string, unknown>;
  const preset = Object.keys(RANGE_LABELS).includes(String(rawRange["preset"]))
    ? (rawRange["preset"] as ReportRangePreset)
    : base.range.preset;
  const range: ReportRange =
    preset === "custom"
      ? {
          preset,
          from: DAY.test(String(rawRange["from"])) ? String(rawRange["from"]) : "",
          to: DAY.test(String(rawRange["to"])) ? String(rawRange["to"]) : "",
        }
      : { preset };

  const rawSort = (raw["sort"] ?? {}) as Record<string, unknown>;
  const by =
    rawSort["by"] === "label" || REPORT_MEASURES.includes(rawSort["by"] as ReportMeasure)
      ? (rawSort["by"] as ReportSort["by"])
      : "label";

  return {
    source,
    groupBy,
    thenBy: thenBy === groupBy ? null : thenBy,
    measures: measures.length > 0 ? measures : ["rows"],
    filters,
    includeGone: raw["includeGone"] === true,
    range,
    sort: { by, dir: rawSort["dir"] === "desc" ? "desc" : "asc" },
  };
}

/* ---------------------------------------------------------------- dates -- */

const iso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** "2026-10-05" as a local date at noon, so no zone moves the day. */
const parseDay = (day: string): Date => {
  const [y = 1970, m = 1, d = 1] = day.split("-").map(Number);
  return new Date(y, m - 1, d, 12);
};

const addDays = (day: string, days: number): string => {
  const date = parseDay(day);
  date.setDate(date.getDate() + days);
  return iso(date);
};

/** The days a range covers, both ends included, counted back from `today`. */
export function resolveRange(range: ReportRange, today: string): { from: string; to: string } {
  const now = parseDay(today);
  switch (range.preset) {
    case "this_month":
      return { from: `${today.slice(0, 8)}01`, to: today };
    case "last_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1, 12);
      const last = new Date(now.getFullYear(), now.getMonth(), 0, 12);
      return { from: iso(first), to: iso(last) };
    }
    case "30d":
      return { from: addDays(today, -29), to: today };
    case "90d":
      return { from: addDays(today, -89), to: today };
    case "ytd":
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case "12m":
      return { from: addDays(today, -364), to: today };
    case "custom": {
      const from = range.from && DAY.test(range.from) ? range.from : addDays(today, -364);
      const to = range.to && DAY.test(range.to) ? range.to : today;
      return from <= to ? { from, to } : { from: to, to: from };
    }
  }
}

/** Whole days from one calendar day to another. */
export const daysBetween = (from: string, to: string): number =>
  Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86_400_000);

/* --------------------------------------------------------------- labels -- */

/** Names for the ids in the facts, from the screen's taxonomy and vocabulary. */
export interface ReportLabels {
  categories: ReadonlyMap<string, string>;
  /** Full paths: "Warehouse › Cage B". */
  locations: ReadonlyMap<string, string>;
  tags: ReadonlyMap<string, string>;
  clients: ReadonlyMap<string, string>;
  /** One step up the location tree, so a shelf filter takes in its bins. */
  parentOf: (locationId: string) => string | null;
}

const NONE_LABELS: Partial<Record<ReportDimension, string>> = {
  category: "No category",
  location: "Not filed anywhere",
  tag: "No tags",
  client: "No client",
  vendor: "No vendor",
  condition: "Condition not given",
  tracking: "Not stock",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** What a group key reads as. "" is the dimension's "none". */
export function keyLabel(
  dim: ReportDimension,
  key: string,
  labels: ReportLabels,
  titles: ReadonlyMap<string, string>,
) {
  if (key === "") return NONE_LABELS[dim] ?? "None";
  switch (dim) {
    case "category":
      return labels.categories.get(key) ?? "A deleted category";
    case "location":
      return labels.locations.get(key) ?? "A deleted location";
    case "tag":
      return labels.tags.has(key) ? `#${labels.tags.get(key)}` : "A deleted tag";
    case "client":
      return labels.clients.get(key) ?? "A deleted client";
    case "status":
      return STATUS_LABELS[key as ItemStatus] ?? key;
    case "condition":
      return CONDITION_LABELS[key as ItemCondition] ?? key;
    case "place":
      return PLACE_LABELS[key as HoldingPlace] ?? key;
    case "tracking":
      return key === "bulk" ? "Bulk" : key === "serialized" ? "Serialized" : key;
    case "kind":
      return MOVEMENT_LABELS[key as MovementKind] ?? key;
    case "month": {
      const [year, month] = key.split("-");
      return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
    }
    case "item":
      return titles.get(key) || "Untitled item";
    case "vendor":
      return key;
  }
}

/* -------------------------------------------------------------- running -- */

type Fact = HoldingFact | ActivityFact;

/** The keys a fact falls under for one dimension. A tag gives one per tag; "" is "none". */
function keysOf(fact: Fact, dim: ReportDimension): string[] {
  switch (dim) {
    case "category":
      return [fact.categoryId ?? ""];
    case "client":
      return [fact.clientId ?? ""];
    case "vendor":
      return [fact.vendor];
    case "item":
      return [fact.itemId];
    case "location":
      return ["locationId" in fact ? (fact.locationId ?? "") : ""];
    case "tag":
      return "tagIds" in fact ? (fact.tagIds.length > 0 ? fact.tagIds : [""]) : [""];
    case "status":
      return ["status" in fact ? fact.status : ""];
    case "condition":
      return ["condition" in fact ? fact.condition : ""];
    case "place":
      return ["place" in fact ? fact.place : ""];
    case "tracking":
      return ["tracking" in fact ? (fact.tracking ?? "") : ""];
    case "month":
      return ["on" in fact ? fact.on.slice(0, 7) : ""];
    case "kind":
      return ["kind" in fact ? fact.kind : ""];
  }
}

function passes(fact: Fact, filters: ReportSpec["filters"], labels: ReportLabels): boolean {
  for (const [key, wanted] of Object.entries(filters) as [ReportDimension, string | undefined][]) {
    if (wanted === undefined) continue;
    if (key === "location" && wanted !== "" && "locationId" in fact) {
      // A shelf takes in everything filed below it, as the Items screen does.
      let at = fact.locationId;
      let found = false;
      for (let steps = 0; at !== null && steps < 64 && !found; steps += 1) {
        found = at === wanted;
        at = labels.parentOf(at);
      }
      if (!found) return false;
      continue;
    }
    if (!keysOf(fact, key).includes(wanted)) return false;
  }
  return true;
}

export type MeasureValues = Record<ReportMeasure, number>;

export interface ReportRow {
  /** One key per level: the group, then the second level when there is one. */
  keys: string[];
  labels: string[];
  values: MeasureValues;
}

export interface ReportResult {
  source: ReportSource;
  dimensions: ReportDimension[];
  measures: ReportMeasure[];
  rows: ReportRow[];
  /**
   * Everything that passed the filters, counted once: a thing with two tags,
   * or with units in two places, is in two rows above but one item here.
   */
  total: MeasureValues;
  /** Activity only: the days counted. */
  range: { from: string; to: string } | null;
  /** Rows with units but no price, which add nothing to Value. */
  unpriced: number;
}

const zero = (): MeasureValues => ({ rows: 0, units: 0, value: 0, cost: 0 });

function add(into: MeasureValues, fact: Fact): void {
  into.rows += 1;
  into.units += fact.units;
  // Stock is priced each. An ordinary item's price is the item's, however
  // many it is -- the same reading as the Reports summary and a listing.
  const lot = "place" in fact && fact.place === "item";
  into.value += (fact.priceCents ?? 0) * (lot ? 1 : fact.units);
  into.cost += (fact.costCents ?? 0) * fact.units;
}

/** The rows a spec starts from, before its filters: the range's movements, or what is held. */
function poolFor(spec: ReportSpec, facts: ReportFacts, today: string): Fact[] {
  if (spec.source === "activity") {
    const range = resolveRange(spec.range, today);
    return facts.activity.filter((fact) => fact.on >= range.from && fact.on <= range.to);
  }
  return facts.holdings.filter((fact) => spec.includeGone || !fact.gone);
}

/**
 * A filter chip's choices for one dimension: only the keys the rows in play
 * have, with how many rows have each, "none" last. Other filters are not
 * applied, so picking one chip never empties another's list.
 */
export function filterChoices(
  spec: ReportSpec,
  facts: ReportFacts,
  dim: ReportDimension,
  labels: ReportLabels,
  today: string,
): { value: string; label: string; count: number }[] {
  const pool = poolFor(spec, facts, today);
  const titles = new Map(pool.map((fact) => [fact.itemId, fact.title]));
  const counts = new Map<string, number>();
  for (const fact of pool) for (const key of keysOf(fact, dim)) counts.set(key, (counts.get(key) ?? 0) + 1);
  return [...counts]
    .map(([value, count]) => ({ value, count, label: keyLabel(dim, value, labels, titles) }))
    .sort((a, b) => (a.value === "" ? 1 : b.value === "" ? -1 : a.label.localeCompare(b.label)));
}

export function runReport(
  spec: ReportSpec,
  facts: ReportFacts,
  labels: ReportLabels,
  today: string,
): ReportResult {
  const range = spec.source === "activity" ? resolveRange(spec.range, today) : null;
  const kept = poolFor(spec, facts, today).filter((fact) => passes(fact, spec.filters, labels));
  const titles = new Map(kept.map((fact) => [fact.itemId, fact.title]));

  const dimensions = spec.thenBy ? [spec.groupBy, spec.thenBy] : [spec.groupBy];
  const groups = new Map<string, ReportRow>();
  const total = zero();
  // In what you hold, "Items" is things, not rows: a bulk line on the shelf
  // and at two clients is three rows and one item. Counted as sets, then
  // written into `rows` at the end. Activity counts lines, which are rows.
  const distinct = spec.source === "holdings";
  const itemsIn = new Map<ReportRow | "total", Set<string>>();
  const count = (into: ReportRow | "total", fact: Fact) => {
    if (!distinct) return;
    const set = itemsIn.get(into) ?? new Set<string>();
    set.add(fact.itemId);
    itemsIn.set(into, set);
  };
  let unpriced = 0;
  for (const fact of kept) {
    add(total, fact);
    count("total", fact);
    if (fact.priceCents === null && fact.units > 0) unpriced += 1;
    const firsts = keysOf(fact, spec.groupBy);
    const seconds = spec.thenBy ? keysOf(fact, spec.thenBy) : [null];
    for (const first of firsts) {
      for (const second of seconds) {
        const keys = second === null ? [first] : [first, second];
        const id = JSON.stringify(keys);
        let row = groups.get(id);
        if (!row) {
          row = {
            keys,
            labels: keys.map((key, level) => keyLabel(dimensions[level]!, key, labels, titles)),
            values: zero(),
          };
          groups.set(id, row);
        }
        add(row.values, fact);
        count(row, fact);
      }
    }
  }
  if (distinct) {
    for (const row of groups.values()) row.values.rows = itemsIn.get(row)?.size ?? 0;
    total.rows = itemsIn.get("total")?.size ?? 0;
  }

  return {
    source: spec.source,
    dimensions,
    measures: spec.measures,
    rows: sortRows([...groups.values()], spec.sort, dimensions),
    total,
    range,
    unpriced,
  };
}

/** "None" rows last whatever the order, months in date order, names alphabetically. */
function sortRows(rows: ReportRow[], sort: ReportSort, dimensions: ReportDimension[]): ReportRow[] {
  const sign = sort.dir === "desc" ? -1 : 1;
  const byLevel = (a: ReportRow, b: ReportRow, level: number, direction: number): number => {
    const ak = a.keys[level] ?? "";
    const bk = b.keys[level] ?? "";
    if ((ak === "") !== (bk === "")) return ak === "" ? 1 : -1;
    const order =
      dimensions[level] === "month"
        ? ak.localeCompare(bk)
        : (a.labels[level] ?? "").localeCompare(b.labels[level] ?? "", undefined, { sensitivity: "base" });
    return order * direction;
  };
  return rows.sort((a, b) =>
    sort.by === "label"
      ? byLevel(a, b, 0, sign) || byLevel(a, b, 1, 1)
      : (a.values[sort.by] - b.values[sort.by]) * sign || byLevel(a, b, 0, 1) || byLevel(a, b, 1, 1),
  );
}

/* ------------------------------------------------------------------ csv -- */

const measureCell = (measure: ReportMeasure, value: number): string =>
  measure === "value" || measure === "cost" ? priceFromCents(value) : String(value);

/** The table as a CSV: plain numbers, amounts as "1234.50", and a total row last. */
export function reportCsv(result: ReportResult): string {
  const header = [
    ...result.dimensions.map((dim) => DIMENSION_LABELS[dim]),
    ...result.measures.map((measure) => measureLabel(measure, result.source)),
  ];
  const lines = [header.map((cell) => csvCell(cell)).join(",")];
  for (const row of result.rows) {
    lines.push(
      [
        ...row.labels.map((label) => csvCell(label)),
        ...result.measures.map((measure) => measureCell(measure, row.values[measure])),
      ].join(","),
    );
  }
  lines.push(
    [
      csvCell("Total"),
      ...result.dimensions.slice(1).map(() => ""),
      ...result.measures.map((measure) => measureCell(measure, result.total[measure])),
    ].join(","),
  );
  return `${lines.join("\r\n")}\r\n`;
}

/* ------------------------------------------------------------- insights -- */

export interface InsightLine {
  itemId: string;
  title: string;
  /** What the line says after the title: "312 days", "14 units". */
  detail: string;
}

export interface ReportInsights {
  /** On the shelf longest, oldest first. Stock only. */
  oldest: InsightLine[];
  /** Most units deployed in the last 90 days. Stock only. */
  fastest: InsightLine[];
  /** On the shelf and not deployed once in the last 90 days, by units. Stock only. */
  idle: InsightLine[];
  /** Held things with no price, and how many. */
  unpriced: { count: number; lines: InsightLine[] };
  /** Held things filed nowhere, and with no category. */
  unfiled: number;
  uncategorised: number;
}

const TOP = 5;

/**
 * The few reports that say what to do rather than only what is. Read from the
 * same facts as the builder; `facts.activity` must reach 90 days back from
 * `today` for the movement-based ones to be right.
 */
export function reportInsights(facts: ReportFacts, today: string): ReportInsights {
  const held = facts.holdings.filter((fact) => !fact.gone);
  const shelf = held.filter((fact) => fact.place === "shelf");
  const since = addDays(today, -89);
  const recentDeploys = facts.activity.filter((fact) => fact.kind === "deployed" && fact.on >= since);
  const plural = (n: number, word: string) => `${n.toLocaleString("en-US")} ${word}${n === 1 ? "" : "s"}`;

  // By title, like the others: six identical laptops received together are
  // one line ("6 units, 336 days"), not six. The oldest unit sets the age.
  const aged = new Map<string, { itemId: string; since: string; units: number }>();
  for (const fact of shelf) {
    const name = fact.title || "Untitled item";
    const now = aged.get(name);
    if (!now) aged.set(name, { itemId: fact.itemId, since: fact.since, units: fact.units });
    else {
      now.units += fact.units;
      if (fact.since < now.since) Object.assign(now, { since: fact.since, itemId: fact.itemId });
    }
  }
  const oldest = [...aged]
    .sort((a, b) => a[1].since.localeCompare(b[1].since) || a[0].localeCompare(b[0]))
    .slice(0, TOP)
    .map(([title, one]) => ({
      itemId: one.itemId,
      title,
      detail: `${plural(one.units, "unit")}, ${plural(daysBetween(one.since, today), "day")} on the shelf`,
    }));

  // By title, not item: twenty identical laptops are one thing that moves.
  const moved = new Map<string, { itemId: string; units: number }>();
  for (const fact of recentDeploys) {
    const name = fact.title || "Untitled item";
    const now = moved.get(name) ?? { itemId: fact.itemId, units: 0 };
    now.units += fact.units;
    moved.set(name, now);
  }
  const fastest = [...moved]
    .sort((a, b) => b[1].units - a[1].units || a[0].localeCompare(b[0]))
    .slice(0, TOP)
    .map(([title, one]) => ({
      itemId: one.itemId,
      title,
      detail: `${plural(one.units, "unit")} out in 90 days`,
    }));

  const deployedTitles = new Set(recentDeploys.map((fact) => fact.title || "Untitled item"));
  const idleByTitle = new Map<string, { itemId: string; units: number }>();
  for (const fact of shelf) {
    const name = fact.title || "Untitled item";
    if (deployedTitles.has(name) || fact.since > since) continue;
    const now = idleByTitle.get(name) ?? { itemId: fact.itemId, units: 0 };
    now.units += fact.units;
    idleByTitle.set(name, now);
  }
  const idle = [...idleByTitle]
    .sort((a, b) => b[1].units - a[1].units || a[0].localeCompare(b[0]))
    .slice(0, TOP)
    .map(([title, one]) => ({
      itemId: one.itemId,
      title,
      detail: `${plural(one.units, "unit")} on the shelf`,
    }));

  // One per item, however many places its units are in.
  const items = new Map<string, HoldingFact>();
  for (const fact of held) if (!items.has(fact.itemId)) items.set(fact.itemId, fact);
  const unpricedItems = [...items.values()].filter((fact) => fact.priceCents === null);

  return {
    oldest,
    fastest,
    idle,
    unpriced: {
      count: unpricedItems.length,
      lines: [
        ...unpricedItems
          .reduce((byTitle, fact) => {
            const name = fact.title || "Untitled item";
            const now = byTitle.get(name) ?? { itemId: fact.itemId, items: 0 };
            now.items += 1;
            return byTitle.set(name, now);
          }, new Map<string, { itemId: string; items: number }>())
          .entries(),
      ]
        .sort((a, b) => b[1].items - a[1].items || a[0].localeCompare(b[0]))
        .slice(0, TOP)
        .map(([title, one]) => ({ itemId: one.itemId, title, detail: plural(one.items, "item") })),
    },
    unfiled: [...items.values()].filter((fact) => fact.locationId === null).length,
    uncategorised: [...items.values()].filter((fact) => fact.categoryId === null).length,
  };
}
